import React, { ReactElement, useEffect, useState, useRef } from 'react';
import { KView, KText, PageContext } from '@/component/RtlLayout';
import { TouchableWithoutFeedback, View, Text } from 'react-native';
import yoda from '@kibt/bridge';
import { convertStyles, localStorage } from '@kibt/krn-utils';
import { $k18n } from '@/common/i18n';
import { SCENE, webCurrency } from '@/common/const';
import Popup from '@kibt/meta-ui-krn/lib/components/Popup';
import { useToast } from '@/component/Toast/Toast';
import { getFreightCharge, FreightChargeStatus, PageRowData } from '../../../api';
import SrcStyles from './index.styl';
const Styles = convertStyles(SrcStyles);

import MyImage from '../MyImage';

import ChooseAddress from '../ChooseAddress';

import { jumpToAddressHalfAddPage } from '../../../jump';
import { logClickAddress, logShowFreightInfo } from '../../../logger2.0';
import { ViewVisible } from '@/component/ViewVisible/ViewVisible';
import { getCookie } from '@/common/cookie';
import { useQuery } from '@/hooks/useQuery';
import { useDetailPageStore } from '@/page/detail/store';
import { reportCatchError } from '@/utils/reportCatchError';
import { scaleW } from '@/common/style';
import Flex from '@/component/atom/Flex';
import { getDecodeDataV3, postAddressQueryApiV3 } from '@/common/api-for-address';
import { hasPermissions } from '@/common/bridge/hasPermissions';
import { useDetailLogger } from '../../../logger3.0';
import { AdjustPCStyle, isIOS, isMobileDeviceValue, PCMaxWidthRemValue } from '@/common/device-info';
import NewUser72Text from '@/component/NewUser72Text';
import { useLoginStore } from '@/component/LoginSdk/store/useLoginStore';

type IProps = {
    pageRawData: PageRowData;
    itemId: number;
    refreshPage: () => void;
    showCompany?: boolean;
};

let getAddressPromiseRs;
const getAddressPromise = new Promise(rs => {
    getAddressPromiseRs = rs;
});

function DeliveryLine(props: IProps): ReactElement {
    const { pageRawData, itemId, refreshPage, showCompany = true } = props;

    const { showToast, toastNode } = useToast();
    const scene = useQuery<string>('scene') || SCENE.DEFAULT_SCENE;

    const [addressList, setAddressList] = useState([]);
    const freightChargeResultCode = useRef(0);
    // logisticsType 1代表物流发货，2代表不需要发货例如线上券码
    // expressFeeType 0非法，1包邮，2统一运费(废弃)，3平台预估运费
    // expressFee 运费
    // displayExpressFee 运费
    const { delivery, addressInfo, addressType } = useDetailPageStore(s => ({
        delivery: s.delivery,
        addressInfo: s.addressInfo,
        addressType: s.addressType,
    }));
    const setStoreState = useDetailPageStore().setState;
    const getStoreState = useDetailPageStore().getState;
    const [addressLoading, setAddressLoading] = useState(true);
    const { logShowAddress, logIfGpsPermission } = useDetailLogger();

    // 是否运费包邮： 现在没有商家包邮的商品，所以不会存在expressFee = 0的情况， 当expressFeeAfterDiscount = 0时，就表示运费券包邮了
    const ifFreeShipping = delivery.expressFeeAfterDiscount === 0;
    // 是否有运费折扣
    const ifShippingDiscount = delivery.expressFee !== delivery.expressFeeAfterDiscount;

    /** 是否展示72小时赔付文字 */
    const show72HourTagText = pageRawData.extraInfo?.userEquity?.length && pageRawData.extraInfo?.userEquity.some(item => item === 1);

    const getAllAddress = async () => {
        try {
            // const addressRes = mockAddress.data;
            const addressRes = await postAddressQueryApiV3(null).catch(err => reportCatchError(err, { position: 'postAddressQueryApiV3' }));
            if (addressRes?.result === 1) {
                let resArr;
                if (addressRes.data?.length) {
                    resArr = addressRes.data.map((item: any) => {
                        return {
                            addressId: item.addressId,
                            userId: item.userId,
                            firstName: item.firstName,
                            lastName: item.lastName,
                            fullName: item.fullName,
                            phone: item.phone,
                            email: item.email,
                            cpf: item.cpf,
                            postalCode: item.postalCode,
                            countryCode: item.countryCode,
                            provinceName: item.provinceName,
                            cityName: item.cityName,
                            districtName: item.districtName,
                            streetName: item.streetName,
                            streetNumber: item.streetNumber,
                            detail: item.detail,
                            complement: item.complement,
                            status: item.status,
                            validFlag: item.validFlag,
                            supportDelivery: item.supportDelivery,
                        };
                    });
                } else {
                    resArr = [];
                }

                setAddressList(resArr);
                // setAddressList([]);
            }
        } catch (err) {
            console.log('获取地址列表挂了', err);
            // state.getAddressError = true;
        }
    };

    const getFreightChargeFromServer = async (params: {
        addressId?: number | null;
        postCode?: string | null;
        itemId?: number;
        province?: string;
        city?: string;
        district?: string;
        scene?: string;
    }) => {
        try {
            // 计算运费接口
            const chargeRes = await getFreightCharge(params);
            if (chargeRes.result === 1) {
                if (chargeRes.data.serviceRule.expressFee !== -1) {
                    setStoreState({ delivery: chargeRes.data.serviceRule, addressInfo: chargeRes.data.address }); // state.delivery = chargeRes.data.serviceRule;
                    return FreightChargeStatus.Success;
                }
            }
            throw new Error('运费计算失败');
        } catch (err: any) {
            if (err?.result) {
                freightChargeResultCode.current = err.result || 0; // state.freightChargeResultCode = err.result || 0;
            }
            // 用来处理计算不出来运费的case
            return FreightChargeStatus.Error;
        }
    };

    // 初始化组件业务逻辑
    useEffect(() => {
        try {
            if (pageRawData && pageRawData.itemId) {
                const fetchData = async () => {
                    try {
                        // 主接口里的用户地址密文
                        const encryAddress = pageRawData?.address?.addressContent || '';
                        const hasUserAddress = pageRawData?.address?.addressContent && +pageRawData?.address?.id !== -9999;
                        if (encryAddress && hasUserAddress) {
                            let decodedAddress;
                            const secretKey = pageRawData?.address?.secretKey;
                            try {
                                decodedAddress = await getDecodeDataV3({
                                    data: {
                                        content: encryAddress,
                                        secretKey,
                                    },
                                });
                                const addressObj = decodedAddress.data as any;
                                // 传给父组件拼上ID，用于跳转购物车
                                setStoreState({
                                    addressType: 'USER',
                                    addressInfo: { ...addressObj, id: decodedAddress.addressId || decodedAddress.id || '' },
                                    delivery: pageRawData.serviceRule,
                                });
                            } catch (e) {
                                setStoreState({
                                    addressType: 'DEFAULT',
                                    addressInfo: pageRawData?.address,
                                    delivery: pageRawData.serviceRule,
                                });
                            }
                        } else {
                            setStoreState({
                                addressType: 'DEFAULT',
                                addressInfo: pageRawData?.address,
                                delivery: pageRawData.serviceRule,
                            });
                        }
                        setAddressLoading(false);

                        getAllAddress();
                    } catch (error) {
                        console.log('error happened in fetchAddressData =>', error);
                    }
                };
                fetchData();
            }
        } catch (error) {}
    }, [pageRawData.address]);

    const currentAddress = () => {
        if (addressType === 'IP') {
            return $k18n({ id: 'k_1250125', text: '获取定位' });
        }
        if (addressInfo) {
            let address = '';
            if (addressInfo.provinceName) {
                address = `${address}${addressInfo.provinceName},`;
            }
            if (addressInfo.cityName) {
                address = `${address}${addressInfo.cityName},`;
            }
            if (addressInfo.districtName) {
                address = `${address}${addressInfo.districtName},`;
            }
            if (addressInfo.streetName) {
                address = `${address}${addressInfo.streetName},`;
            }
            address = address.slice(0, address.length - 1);
            return address;
        }
        return '';
    };

    // 事件相关
    const listener = async data => {
        console.log('进入 listener1');
        console.log('进入 listener1', data);
        if (!data) {
            return;
        }
        // 坑 发送的数据是字符串
        let dispatchObj;
        if (typeof data === 'string') {
            dispatchObj = JSON.parse(data);
        } else {
            dispatchObj = data;
        }

        const tempAddress = {
            'detail-page-address-province': dispatchObj?.province || '',
            'detail-page-address-city': dispatchObj?.city || '',
            'detail-page-address-district': dispatchObj?.district || '',
            'detail-page-address-street': dispatchObj?.street || '',
            'detail-page-address-postalCode': dispatchObj?.postalCode || '',
        };

        await localStorage.setItem('detail-page-temporaryAddress', JSON.stringify(tempAddress));

        setTimeout(() => {
            refreshPage();
        }, 0);
    };

    useEffect(() => {
        const initYoda = async () => {
            try {
                if (yoda) {
                    // await yoda?.ready();
                    console.log(yoda && yoda.event);
                    if (yoda && yoda.event) {
                        console.log('添加事件');
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        yoda.event.addEventListener('create-address-by-half', listener, ({ result }) => {
                            // addEventListener bridge 本身是否调用成功
                            if (result === 1) {
                                // 成功
                                console.log('addEventListener success');
                            } else {
                                // 失败
                                console.log('addEventListener failed');
                            }
                        });
                    }
                    return;
                }
            } catch (error) {
                console.log('挂了1', error);
            }
        };
        initYoda();
    }, []);

    const loginSucceedCB = () => {
        getAllAddress();
    };

    useEffect(() => {
        if (yoda && yoda.event) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            yoda.event.addEventListener('loginSucceed', loginSucceedCB, ({ result }) => {
                if (result === 1) {
                    console.log('********** addEventListener login');
                } else {
                    // 失败
                    console.log('********** addEventListener login failed');
                }
            });
        }
    }, []);

    const [showAddAddressPopup, setShowAddAddressPopup] = useState(false);
    const { isLogin, setLoginModelVisible } = useLoginStore(s => ({
        isLogin: s.isLogin,
        setLoginModelVisible: s.setLoginModelVisible,
    }));

    const openAddAddressPopup = async () => {
        logClickAddress(addressType);
        if (!isLogin) {
            return setLoginModelVisible(true);
        }
        if (addressList.length) {
            setShowAddAddressPopup(true);
        } else {
            jumpToAddressHalfAddPage(itemId);
        }
    };

    // 如果用户地址列表有地址
    // 选用该地址之前
    // 先看当前的商品是否需要计算邮费
    // 用选择的地址 调用接口
    // 如果算不出邮费 把地址做差异化展示 并且toast
    const handleChooseNewAddress = async (address: any, index: number) => {
        // state.showLoading = true;
        const status = await getFreightChargeFromServer({
            addressId: address.addressId || null,
            postCode: address.postalCode,
            itemId: +itemId,
            province: address.provinceName,
            city: address.cityName,
            district: address.districtName,
            scene,
        });

        if (status === FreightChargeStatus.Error && freightChargeResultCode.current !== 7010) {
            // 处理运费计算失败的逻辑
            const addressListCopy = JSON.parse(JSON.stringify(addressList));
            addressListCopy[index].chargeErr = true;
            setAddressList(addressListCopy);
            showToast({
                text: $k18n('该地址不支持配送'),
                duration: 1000,
            });
            return;
        }

        const newAddressInfo = {
            addressId: address.addressId || address.id,
            countryCode: address.countryCode,
            provinceName: address.provinceName,
            cityName: address.cityName,
            districtName: address.districtName,
            streetName: address.streetName,
        };
        setStoreState({
            addressInfo: newAddressInfo,
        });

        // 选中新的有效地址后删除localStorage
        localStorage.removeItem('detail-page-temporaryAddress');

        // state.showLoading = false;
        setShowAddAddressPopup(false);
    };

    const handleAddAddress = () => {
        jumpToAddressHalfAddPage(itemId);
        setShowAddAddressPopup(false);
    };

    // 运费的展示分为三部分 优惠后运费 - 优惠前运费 - 优惠的运费
    const costInfo = () => {
        // 线上不会存在商家包邮的情况
        // 运费券包邮 展示 free + 划线原价 + xxOff https://ali.a.yximgs.com/kos/nlav12119/uodiJJoS_2024-09-11-16-16-27.png
        // 运费折扣 展示 运费 + 划线原运费 + xxxoff https://ali.a.yximgs.com/kos/nlav12119/bPOmGcph_2024-09-11-16-17-54.png
        if (addressLoading) {
            return (
                <View>
                    <Text
                        style={{"fontSize":"3.381642512077295rem","fontWeight":"500","color":"black"}}>
                        {$k18n('待定')}
                    </Text>
                </View>
            );
        }
        if (ifShippingDiscount) {
            return (
                <Flex flex={'flexRowCenter'}>
                    <Text style={{"fontSize":"3.381642512077295rem","fontWeight":"500","color":"rgba(76, 167, 162, 1)"}}>
                        {ifFreeShipping ? $k18n('Free') : delivery.displayExpressFeeAfterDiscount}
                    </Text>
                    {delivery.expressFee ? (
                        <Text
                            style={{"textDecorationLine":"line-through","textDecorationColor":"rgba(153, 153, 153, 1)","fontSize":"2.8985507246376816rem","marginHorizontal":"0.9661835748792271rem","fontWeight":"500","color":"rgba(153, 153, 153, 1)"}}>
                            {delivery.displayExpressFee}
                        </Text>
                    ) : null}
                    {delivery.expressFeeDiscount ? (
                        <View
                            style={{"borderWidth":"0.24154589371980678rem","borderColor":"rgba(76, 167, 162, 0.12)","borderRadius":"0.7246376811594204rem","paddingHorizontal":"0.9661835748792271rem","height":"3.8647342995169085rem"}}>
                            <Text style={{"lineHeight":"3.381642512077295rem","fontSize":"2.8985507246376816rem","color":"rgba(76, 167, 162, 1)"}}>
                                -{delivery.displayExpressFeeDiscount}
                            </Text>
                        </View>
                    ) : null}
                </Flex>
            );
        } else if (pageRawData.discountCoupon?.filter(coupon => coupon?.discountFeeType === 3).length) {
            return (
                <View>
                    <Text
                        style={{"fontSize":"3.381642512077295rem","fontWeight":"500","color":"#4CA7A2"}}>
                        {$k18n('领券包邮')}
                    </Text>
                </View>
            );
        }
        return (
            <View>
                <Text
                    style={{"fontSize":"3.381642512077295rem","fontWeight":"500","color":"black"}}>
                    {delivery.expressFee === -1 ? $k18n('待定') : delivery.displayExpressFee}
                </Text>
            </View>
        );
    };

    const onDeliveryLineShow = () => {
        getAddressPromise.then(() => {
            logShowAddress(getStoreState().addressType);
        });
    };

    return (
        <PageContext.Provider value={{ Styles }}>
            <ViewVisible onVisible={onDeliveryLineShow}>
                <KView rtlClassName={'delivery'}>
                    <KView rtlClassName={'first-line'}>
                        <View>
                            <Text
                                style={{"fontSize":"3.381642512077295rem","fontWeight":"500","color":"black","lineHeight":"3.8647342995169085rem"}}>
                                {$k18n('物流')}
                            </Text>
                        </View>
                        <TouchableWithoutFeedback onPress={() => openAddAddressPopup()}>
                            <KView rtlClassName={'address'}>
                                <KView rtlClassName={'address-icon'}>
                                    <MyImage
                                        imageSrc={{
                                            uri: 'https://cdn-static.kwai.net/kos/s101/nlav11312/shop-krn/Frame194549.png',
                                        }}
                                    />
                                </KView>
                                <KView rtlClassName={'address-name'}>
                                    <KText numberOfLines={1} ellipsizeMode={'tail'} rtlClassName={'address-name-text'}>
                                        {addressLoading ? $k18n('加载中') : currentAddress()}
                                    </KText>
                                </KView>
                                <KView
                                    style={{"marginRight":"-0.24154589371980678rem"}}>
                                    <MyImage
                                        imageSrc={{
                                            uri: 'https://cdn-static.ap4r.com/kos/s101/nlav11312/shop-krn/kibt_post_import_album_icon_arrow_106.png',
                                        }}
                                    />
                                </KView>
                            </KView>
                        </TouchableWithoutFeedback>
                    </KView>
                    <View>
                        <KView rtlClassName={'cost'}>
                            <View>
                                <Text style={{"fontSize":"3.381642512077295rem","color":"black","marginRight":"1.9323671497584543rem"}}>
                                    {$k18n({ id: 'shipping_cost', text: '运费' })}
                                </Text>
                            </View>
                            <ViewVisible onVisible={() => logShowFreightInfo(delivery.displayExpressFee)}>{costInfo()}</ViewVisible>
                        </KView>

                        {show72HourTagText ? (
                            <KView
                                style={{"display":"flex","flexDirection":"row","alignItems":"center","marginTop":"0.9661835748792271rem"}}>
                                <NewUser72Text propsFontWeight="700" propsFontSize={scaleW(12)} propsColor="#4CA7A2" />
                            </KView>
                        ) : null}
                    </View>
                </KView>
            </ViewVisible>
            <Popup
                visible={showAddAddressPopup}
                duration={50}
                onClose={() => {
                    setShowAddAddressPopup(false);
                }}
                position="bottom">
                <PageContext.Provider value={{ Styles }}>
                    <KView
                        rtlClassName={'add-address-popup'}
                        style={{
                            ...AdjustPCStyle,
                        }}>
                        <ChooseAddress
                            addressList={addressList}
                            handleAddAddress={handleAddAddress}
                            handleChooseNewAddress={handleChooseNewAddress}
                        />
                    </KView>
                </PageContext.Provider>
            </Popup>
            {toastNode}
        </PageContext.Provider>
    );
}

export default DeliveryLine;
